// ============================================================================
// <TerritoryMap> — Band 3's choropleth, the panel that had been removed since
// the Finance View shipped.
//
// The recorded blocker was "what is missing is the state geometry… it needs
// either a state TopoJSON added to the bundle or the existing Google Maps
// surface extended with state polygons, and a decision on which". Ben's
// decision: build it on Maps. So:
//
//   * The geometry is in `territory-shapes.ts` — generated offline, simplified,
//     461 points, no runtime dependency and no TopoJSON in the bundle.
//   * TEXAS IS TWO SHAPES, cut at 31.5 N, because Texas carries two territories
//     and that is precisely why a state map was said to be impossible here.
//   * Google Maps is already loaded by this app for the facility map, so this
//     adds no dependency and no second key.
//
// COLOUR IS TERRITORY, NEVER MARGIN. Jose's rule, and it is the whole reason
// the map exists: the reader matches a shape to a row in the cards above. A
// margin-coloured map would collide with the green/amber/red the rest of the
// page uses for status.
//
// ⛔ TWO TRAPS, BOTH PAID FOR IN A PRODUCTION BLANK SCREEN ON 2026-08-20:
//
//  1. `useJsApiLoader` MUST NOT BE CALLED BEFORE THE KEY ARRIVES. The key is
//     fetched at runtime (`get_maps_browser_key`), so the first render has
//     `null`. Calling the loader with `''` and then re-rendering with the real
//     key hits `@googlemaps/js-api-loader`'s "loader must not be called again
//     with different options" — it is a SINGLETON keyed by `id`, and the throw
//     is not caught by anything, so the whole Executive Overview rendered as an
//     empty <main>. The fix is structural, not defensive: the outer component
//     owns the key and does not render the inner one until it is a non-empty
//     string, so the loader is only ever called once, correctly.
//
//  2. A DECORATIVE PANEL MUST NOT BE ABLE TO TAKE THE PAGE DOWN. Even with (1)
//     fixed, the map is the only thing on this page that depends on a third
//     party at runtime. It is wrapped in an error boundary that degrades to the
//     same removed tile the panel used for six days. Every number on Band 3 is
//     computed without it.
//
// ⚠️ `token()` is called at RENDER, never at module scope. A module-scope const
// evaluates before Vite injects the stylesheet in dev and resolves to the
// literal string `var(--terr-kc)`, which Google Maps rejects — the same trap
// map-colors.ts documents.
// ============================================================================

import { Component, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { GoogleMap, PolygonF, useJsApiLoader } from '@react-google-maps/api';
import { token } from '../../shared/design-tokens';
import { useTheme } from '../../shared/theme';
import { useMapsBrowserKey } from '../../shared/map/use-map-data';
import { RemovedTile } from './finance-chrome';
import {
  TERRITORY_ORDER,
  TERRITORY_STYLES,
  TERRITORY_COLOUR_NAME,
  fmtMoneyCompact,
  fmtPct,
  type Territory,
} from './finance-lib';
import { TERRITORY_SHAPES } from './territory-shapes';

export interface TerritorySlice {
  territory: Territory;
  buildings: number;
  arr: number;
  marginPct: number | null;
}

/** Continental-US framing that puts every territory on screen at once. */
const CENTER = { lat: 37.4, lng: -97.5 };
const ZOOM = 4;
const CONTAINER = { width: '100%', height: '420px' };

function MapSkeleton() {
  return (
    <div
      className="mt-3 animate-pulse rounded-lg border border-tcs-border bg-tcs-raised"
      style={{ height: CONTAINER.height }}
      aria-label="Loading the territory map"
    />
  );
}

function MapUnavailable({ reason }: { reason: string }) {
  return (
    <div className="mt-3">
      <RemovedTile title="The territory choropleth" prerequisite={reason} />
    </div>
  );
}

/**
 * The map cannot be allowed to blank the page. See trap 2 above — this is the
 * net, not the fix.
 */
class MapBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <MapUnavailable reason="The map failed to render. Every territory figure on this band is computed without it and is unaffected — the colours, the state lists and the numbers are all above." />
      );
    }
    return this.props.children;
  }
}

export function TerritoryMap({ slices }: { slices: TerritorySlice[] }) {
  const { key, keyLoading } = useMapsBrowserKey();

  if (keyLoading) return <MapSkeleton />;

  // ⛔ Do NOT hand an empty key to useJsApiLoader — see trap 1.
  if (!key) {
    return (
      <MapUnavailable reason="A Google Maps browser key. It is fetched at sign-in from the vault via get_maps_browser_key and is never bundled; this account did not receive one. Every number the map would carry is present above." />
    );
  }

  return (
    <MapBoundary>
      <TerritoryMapCanvas slices={slices} apiKey={key} />
    </MapBoundary>
  );
}

function TerritoryMapCanvas({ slices, apiKey }: { slices: TerritorySlice[]; apiKey: string }) {
  const { resolved } = useTheme();
  const [hovered, setHovered] = useState<Territory | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Same `id` as the facility map on purpose: one SDK load per page, and the
  // key is identical because it comes from the same RPC.
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'tcs-google-maps',
    googleMapsApiKey: apiKey,
  });

  const byTerritory = useMemo(() => {
    const m = new Map<Territory, TerritorySlice>();
    for (const s of slices) m.set(s.territory, s);
    return m;
  }, [slices]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);
  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // A territory we hold figures for but have no shape for is NAMED, never
  // silently skipped — otherwise the map quietly under-reports the business.
  const unmapped = TERRITORY_ORDER.filter(
    (t) => byTerritory.has(t) && (TERRITORY_SHAPES[t]?.length ?? 0) === 0,
  );

  if (loadError) {
    return (
      <MapUnavailable reason="The Google Maps SDK failed to load. The territory colours, the legend, the state lists and every number the map would carry are all present above — the map is the only thing missing, and none of the figures depend on it." />
    );
  }
  if (!isLoaded) return <MapSkeleton />;

  return (
    <div className="mt-3">
      <div className="overflow-hidden rounded-lg border border-tcs-border">
        <GoogleMap
          key={resolved}
          mapContainerStyle={CONTAINER}
          center={CENTER}
          zoom={ZOOM}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={
            {
              // A reporting map, not a navigation one: nothing here invites a
              // click that means anything.
              disableDefaultUI: true,
              zoomControl: true,
              gestureHandling: 'cooperative',
              colorScheme: resolved === 'dark' ? 'DARK' : 'LIGHT',
              styles: [
                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                { featureType: 'road', stylers: [{ visibility: 'off' }] },
              ],
            } as google.maps.MapOptions
          }
        >
          {TERRITORY_ORDER.flatMap((terr) => {
            const rings = TERRITORY_SHAPES[terr] ?? [];
            const slice = byTerritory.get(terr);
            const colour = token(TERRITORY_STYLES[terr].cssVar);
            const isHot = hovered === terr;
            // A territory with no buildings still draws, faintly — "we are not
            // there" and "we are there and it is small" must not look the same.
            const present = (slice?.buildings ?? 0) > 0;
            return rings.map((path, i) => (
              <PolygonF
                key={`${terr}-${i}`}
                paths={path}
                options={{
                  fillColor: colour,
                  fillOpacity: present ? (isHot ? 0.72 : 0.5) : 0.12,
                  strokeColor: colour,
                  strokeOpacity: present ? 1 : 0.45,
                  strokeWeight: isHot ? 2.5 : 1.25,
                  clickable: true,
                }}
                onMouseOver={() => setHovered(terr)}
                onMouseOut={() => setHovered((h) => (h === terr ? null : h))}
              />
            ));
          })}
        </GoogleMap>
      </div>

      {/* The legend is the contract between the map and the cards above it. It
          repeats the colour NAME because this gets printed into a deck and read
          by people matching a shape to a row. */}
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-tcs-text-secondary">
        {TERRITORY_ORDER.map((terr) => {
          const slice = byTerritory.get(terr);
          return (
            <li
              key={terr}
              className={`flex items-center gap-1.5 ${hovered === terr ? 'text-tcs-text' : ''}`}
              onMouseEnter={() => setHovered(terr)}
              onMouseLeave={() => setHovered((h) => (h === terr ? null : h))}
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-sm ${TERRITORY_STYLES[terr].swatchClass}`}
                aria-hidden
              />
              <span className="text-tcs-text">{terr}</span>
              <span className="text-tcs-text-muted">
                {TERRITORY_COLOUR_NAME[terr]} ·{' '}
                {slice ? (
                  <>
                    {fmtMoneyCompact(slice.arr)} ARR · {fmtPct(slice.marginPct)} ·{' '}
                    {slice.buildings} building{slice.buildings === 1 ? '' : 's'}
                  </>
                ) : (
                  'no buildings'
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <p className="mt-2 text-[11px] text-tcs-text-muted">
        Colour is <strong className="text-tcs-text-secondary">territory, not margin</strong>.
        Texas is drawn as two shapes because it carries two territories; the line is at
        31.5°N and is a reading aid — a building&apos;s territory comes from its{' '}
        <code>market_region</code>, never from where it falls on this map. Only states we
        operate in are drawn.
        {unmapped.length > 0 && (
          <>
            {' '}
            <span className="text-st-warn-fg">
              No shape for {unmapped.join(', ')} — {unmapped.length === 1 ? 'it is' : 'they are'}{' '}
              in the figures above but not on the map.
            </span>
          </>
        )}
      </p>
    </div>
  );
}
